import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-devops-guide',
  imports: [CommonModule, RouterModule],
  standalone: true,
  templateUrl: './devops-guide.component.html',
  styleUrl: './devops-guide.component.scss'
})
export class DevopsGuideComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  private jsonLdScript: any;
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: any) => route.data)
    )
      .subscribe((event: any) => {
        if (this.router.url === '/blogs/devops-guide') {
          this.updateDescription(event.description, event.keywords, event.title);
        }
      });
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const devopsSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/devops-guide#webpage",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/devops-guide",
          "name": "The Ultimate Guide to DevOps: From Evolution to Implementation",
          "description": "Complete guide to DevOps covering definition, history and evolution from 2009 to 2024, CI/CD process, and tools.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2024-10-01"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/devops-guide#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/blog", "name": "Blog Index" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/devops-guide", "name": "The Ultimate Guide to DevOps" } }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/devops-guide#blogposting",
          "headline": "The Ultimate Guide to DevOps: From Evolution to Implementation",
          "articleBody": "DevOps has transformed software development by bridging the gap between development and deployment, enabling faster and more reliable delivery...",
          "author": { "@type": "Person", "name": "Grace Catharine" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2024-10-01",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/devops-guide/devops-guide-banner.webp"
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/devops-guide#howto",
          "name": "How to Implement a DevOps CI/CD Pipeline for Custom Applications",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Set Up ALM and Version Control", "text": "Use tools like Jira or Azure DevOps for managing requirements and Git for version control." },
            { "@type": "HowToStep", "position": 2, "name": "Code Review and Quality Analysis", "text": "Review code and use SonarQube to assess quality standards." },
            { "@type": "HowToStep", "position": 3, "name": "Build and Test", "text": "Trigger build process, upload to Nexus, and conduct unit/security testing." },
            { "@type": "HowToStep", "position": 4, "name": "Deploy Through Environments", "text": "Deploy to CI, QA, UAT, and Pre-Prod before promoting to production." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/devops-guide#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is DevOps?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Gartner defines DevOps as a representation of change in IT culture that focuses on rapid IT service delivery through the adoption of agile, lean practices."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(devopsSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  updateDescription(desc: string, keywords: string, title: string) {
    // this.seoService.updateDescription(desc);
    // this.seoService.updateMetaKeywords(keywords);
    // this.seoService.updateTitle(title);
  }

  @ViewChild('tableresponsiveImage') image!: ElementRef<HTMLImageElement>;

  originalWidth = 7227;
  originalHeight = 880;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  @HostListener('window:resize')
  resizeImageMap() {
    if (!this.image?.nativeElement) return;

    const img = this.image.nativeElement;
    const widthRatio = img.clientWidth / this.originalWidth;
    const heightRatio = img.clientHeight / this.originalHeight;

    document
      .querySelectorAll('map[name="table-image-map"] area')
      .forEach((area: any) => {
        let coords = area.getAttribute('data-original-coords')
          || area.getAttribute('coords');
        if (!coords) return;

        area.setAttribute('data-original-coords', coords);
        const scaledCoords = coords
          .split(',')
          .map((coord: any, index: number) =>
            Math.round(
              Number(coord) * (index % 2 === 0 ? widthRatio : heightRatio)
            )
          );
        area.setAttribute('coords', scaledCoords.join(','));
      });
  }

  scrollToSection(sectionId: string): void {
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const offset = 5 * 16;
      const scrollPosition = rect.top + window.pageYOffset - offset;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }
}
